/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';

import {LocalStorage} from "./local-storage";
import {LocalNetworkProfile} from './local-network-profile';

const USER_PERMISSIONS_STORAGE_KEY = "user.permissions";

function createPermissionsService(LocalNetworkProfile: LocalNetworkProfile): any {
    function politicalAppOnNetwork() {
      return !!LocalNetworkProfile.get('politicalApp');
    }

    // CONTROLLER / SPONSOR are team roles only; ignore if present on POLITICAL networkPermissions.
    const POLITICAL_TEAM_ONLY = ['CONTROLLER', 'CONTROLLERS', 'SPONSOR'];

    function Profile(permissions, appsByNetwork, createTime, readOnlyTeams) {
      this.permissions = permissions;
      this.appsByNetwork = appsByNetwork;
      this.createTime = createTime;
      this.readOnlyTeams = readOnlyTeams;

      const collection = _.map(this.permissions, function(val) {
        return val.teamPermissions;
      });
      this.allTeams = _.filter(_.flatten(_.map(collection, function(val) {
        return _.map(val, function(val, teamId) {
          return teamId;
        });
      })).concat(this.readOnlyTeams), function(val) {
        return val && !_.isEmpty(val);
      });

      this.allNetworks = _.map(this.permissions, function(val, key) {
        return key;
      });
    }

    Profile.prototype.isPartOf = function(networkId, groupId) {
      return !!_.find(this.permissions[networkId], function(s) {
        const permArray = _.get(s, groupId);
        return _.isArray(permArray) && permArray.length > 0;
      });
    };

    Profile.prototype.getPermissions = function(teamId) {
      const networkId = LocalNetworkProfile.getNetworkId();
      let teamPermissions = _.get(this.permissions, [networkId, 'teamPermissions']) || {};
      if (politicalAppOnNetwork()) {
        const politicalTeams = _.get(this.permissions, [networkId, 'POLITICAL', 'teamPermissions']) || {};
        teamPermissions = _.assign({}, teamPermissions, politicalTeams);
      }
      return _.flatten(_.map(teamPermissions, function(val, key) {
        if (teamId) {
          return teamId === key ? val : [];
        }
        return val;
      }));
    };

    Profile.prototype.isReadOnlyForTeamType = function(teamTypeToCheck) {
      const networkId = LocalNetworkProfile.getNetworkId();
      return _.reduce(_.flatten(_.map(_.get(this.readOnlyTeams, [networkId, 'teams']), function(teamType) {
        return teamType === teamTypeToCheck;
      })), function(sum, n) {
        return n || sum;
      }, false);
    };

    Profile.prototype.getNetworkPermissions = function(networkId, app) {
      const objPath = [networkId || LocalNetworkProfile.getNetworkId()];
      if (app) {
        objPath.push(app);
      }
      objPath.push('networkPermissions');
      const perms = _.get(this.permissions, objPath) || [];
      return app === 'POLITICAL' ? _.filter(perms, function(p) {
        return POLITICAL_TEAM_ONLY.indexOf(p) < 0;
      }) : perms;
    };

    Profile.prototype.getTeamCount = function(networkId) {
      const networkPerms = _.get(this.permissions, networkId);
      if (!networkPerms) {
        return 0;
      }
      const teamIds = {};
      const addTeams = function(teamPermissions) {
        _.forEach(teamPermissions, function(val, teamId) {
          if (teamId) {
            teamIds[teamId] = true;
          }
        });
      };
      addTeams(networkPerms.teamPermissions);
      _.forEach(networkPerms, function(block, key) {
        if (key !== 'networkPermissions' && key !== 'teamPermissions' && _.isObject(block)) {
          addTeams((block as any).teamPermissions);
        }
      });
      return _.size(teamIds);
    };

    Profile.prototype.hasAnyPermission = function(permission) {
      return _.includes(this.getPermissions(), permission);
    };

    Profile.prototype.hasPermission = function(permission, groupId) {
      return _.includes(this.getPermissions(groupId), permission);
    };

    Profile.prototype.hasNetworkPermission = function(permission, networkId, app) {
      return _.includes(this.getNetworkPermissions(networkId || LocalNetworkProfile.getNetworkId(), app), permission);
    };

    Profile.prototype.save = function() {
      LocalStorage.obtain().setItem(USER_PERMISSIONS_STORAGE_KEY, this);
      return this;
    };

    Profile.fromObj = function(obj) {
      if (_.isObject(obj)) {
        return _.create(Profile.prototype, obj);
      }
      return null;
    };

    let instance = null;

    function ensureInstance() {
      if (!instance) {
        // Load lazily — construction often runs before AppRuntimeLifecycleService
        // sets the LocalStorage prefix (adn.<apiId>), so an eager read misses
        // adn.<apiId>.user.permissions and leaves menus empty.
        instance = Profile.fromObj(LocalStorage.obtain().getItem(USER_PERMISSIONS_STORAGE_KEY));
      }
      return instance;
    }

    return {
      create: function(authUser) {
        instance = new Profile(authUser.permissions, authUser.appsByNetwork, _.get(authUser, ['user', 'createTime']), authUser.readOnlyTeams);
        return instance.save();
      },
      destroy: function() {
        instance = null;
        return LocalStorage.obtain().remove(USER_PERMISSIONS_STORAGE_KEY);
      },
      get: function(prop) {
        const current = ensureInstance();
        return prop ? _.result(current, prop) : current;
      },
      isPartOf: function(networkId, teamId) {
        return ensureInstance() ? instance.isPartOf(networkId, teamId) : false;
      },
      userPartOf: function(user, tagId) {
        return !!_.find(_.get(user, 'userRoles.rolesByNetwork') || [], function(role) {
          return _.get(role, "network.tagId") === tagId || _.get(role, "network.id") === tagId;
        });
      },
      hasAny: function(permission) {
        return ensureInstance() ? instance.hasAnyPermission(permission) : false;
      },
      hasAnyReadOnly: function(teamType) {
        return ensureInstance() ? instance.isReadOnlyForTeamType(teamType) : false;
      },
      has: function(permission, groupId) {
        return ensureInstance() ? instance.hasPermission(permission, groupId) : false;
      },
      hasNetworkPermission: function(permission, networkId, app) {
        return ensureInstance() ? instance.hasNetworkPermission(permission, networkId, app) : false;
      },
      canEditPoliticalResources: function() {
        if (!ensureInstance()) {
          return false;
        }
        if (instance.hasNetworkPermission('MANAGE_SYSTEM') || instance.hasAnyPermission('MANAGE_SYSTEM')) {
          return true;
        }
        if (!politicalAppOnNetwork()) {
          return false;
        }
        return instance.hasNetworkPermission('POLITICAL_ADMIN', null, 'POLITICAL')
          || _.some(POLITICAL_TEAM_ONLY, function(p) {
            return instance.hasAnyPermission(p);
          });
      },
      getApps: function(networkId) {
        ensureInstance();
        const nid = networkId || LocalNetworkProfile.getNetworkId();
        const apps = _.cloneDeep(_.get(instance, ['appsByNetwork', nid])) || [];
        if (apps.length === 0 && apps.indexOf("ADVERTISING") < 0) {
          apps.push("ADVERTISING");
        }
        if (LocalNetworkProfile.isSuiPlatform()) {
          apps.push("SELF_SERVICE");
        }
        if (politicalAppOnNetwork() && apps.indexOf('POLITICAL') < 0 && instance) {
          const politicalBlock = _.get(instance.permissions, [nid, 'POLITICAL']);
          if (politicalBlock && (
            instance.getNetworkPermissions(nid, 'POLITICAL').length > 0
            || !_.isEmpty(politicalBlock.teamPermissions)
          )) {
            apps.push('POLITICAL');
          }
        }
        return apps;
      },
      getNetworkWithMostTeams: function(networkIds) {
        if (!ensureInstance() || !_.isArray(networkIds) || networkIds.length === 0) {
          return null;
        }
        let bestNetworkId = null;
        let bestCount = -1;
        _.forEach(networkIds, function(networkId) {
          const count = instance.getTeamCount(networkId);
          if (count > bestCount) {
            bestCount = count;
            bestNetworkId = networkId;
          }
        });
        return bestNetworkId;
      },
      getNetworkPermissionsObject: function(networkId) {
        if (!ensureInstance()) {
          return {};
        }
        const nid = networkId || LocalNetworkProfile.getNetworkId();
        const permissionsObject = {};
        _.forEach(this.getApps(), function(app) {
          _.forEach(instance.getNetworkPermissions(nid, app === 'ADVERTISING' ? null : app), function(p) {
            permissionsObject[p] = true;
          });
        });
        if (politicalAppOnNetwork()) {
          _.forEach(instance.getNetworkPermissions(nid, 'POLITICAL'), function(p) {
            permissionsObject[p] = true;
          });
        }
        return permissionsObject;
      },
      getAllVisPermissions: function(networkId) {
        return _.merge({}, this.getAllNetworkPermissions(networkId), this.getAnyVisibility());
      },
      getAllNetworkPermissions: function(networkId) {
        const showAllDefault: Record<string, boolean> = {
          MANAGE_SYSTEM: true,
          MANAGE_LAYOUTS: true,
          MANAGE_REPORT_TEMPLATES: true,
          MANAGE_BUSINESS: true,
          SELF_SERVICE_USER_ADMIN: true,
          DATA_ADMIN: false,
          DOWNLOAD_LOGS: false
        };
        const networkPermissionsObject = this.getNetworkPermissionsObject(networkId);
        if (LocalNetworkProfile.getDefaults().appSectionVisibility === 'SHOW_ALL') {
          if (networkPermissionsObject.DATA_ADMIN) {
            showAllDefault.DATA_ADMIN = true;
          }
          if (networkPermissionsObject.DOWNLOAD_LOGS) {
            showAllDefault.DOWNLOAD_LOGS = true;
          }
          if (networkPermissionsObject.POLITICAL_ADMIN) {
            showAllDefault.POLITICAL_ADMIN = true;
          }
          return showAllDefault;
        }
        return networkPermissionsObject;
      },
      getAnyVisibility: function(getActuals) {
        const showAllDefaults = {
          MANAGE_ADVERTISING: true,
          MANAGE_PUBLISHING: true,
          MANAGE_AUDIENCES: false,
          AX_PUBLISHER: false,
          AX_ADVERTISER: false,
          RUN_REPORTS: true,
          SELF_SERVICE_OWN_ADVERTISING: false
        };
        if (!getActuals && LocalNetworkProfile.getDefaults().appSectionVisibility === 'SHOW_ALL') {
          return showAllDefaults;
        }
        return {
          SELF_SERVICE_OWN_ADVERTISING: this.hasAny('SELF_SERVICE_OWN_ADVERTISING'),
          MANAGE_ADVERTISING: this.hasAny('MANAGE_ADVERTISING'),
          MANAGE_PUBLISHING: this.hasAny('MANAGE_PUBLISHING'),
          MANAGE_AUDIENCES: this.hasAny('MANAGE_AUDIENCES'),
          AX_ADVERTISER: this.hasAny('AX_ADVERTISER'),
          AX_ADVERTISER_ADMIN: this.hasNetworkPermission('AX_ADVERTISER_ADMIN'),
          CREATIVE_AGENCY: this.hasAny('CREATIVE_AGENCY'),
          AX_ADVERTISER_READ_ONLY: this.hasAnyReadOnly('AX_ADVERTISER'),
          AX_ADVERTISER_TEAM_ADMIN: this.hasAny('AX_ADVERTISER_TEAM_ADMIN'),
          AX_PUBLISHER_TEAM_ADMIN: this.hasAny('AX_PUBLISHER_TEAM_ADMIN'),
          AX_PUBLISHER: this.hasAny('AX_PUBLISHER'),
          AX_PUBLISHER_ADMIN: this.hasNetworkPermission('AX_PUBLISHER_ADMIN'),
          AX_PUBLISHER_READ_ONLY: this.hasAnyReadOnly('AX_PUBLISHER'),
          RUN_REPORTS: this.hasAny('RUN_REPORTS'),
          DATA_ADMIN: this.hasAny('DATA_ADMIN')
        };
      },
      canBeShownAdvertiser: function() {
        return !this.isSelfService() && !this.isAxPublisherOnly();
      },
      isNotSelfService: function() {
        return !this.isSelfService() && !this.isAxPublisherAdminOnly() && !this.isAxPublisherOnly();
      },
      isSelfServiceOwn: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.MANAGE_ADVERTISING === false && anyPerms.MANAGE_PUBLISHING === false && anyPerms.SELF_SERVICE_OWN_ADVERTISING === true;
      },
      isSelfService: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.MANAGE_ADVERTISING === false &&
          anyPerms.MANAGE_PUBLISHING === false &&
          anyPerms.SELF_SERVICE_OWN_ADVERTISING === true;
      },
      isAdOps: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.AX_ADVERTISER !== true && anyPerms.MANAGE_ADVERTISING === true;
      },
      isAxAdvertiser: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.AX_ADVERTISER === true && anyPerms.MANAGE_ADVERTISING !== true;
      },
      isAnyAxPublisher: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.AX_PUBLISHER === true || anyPerms.AX_PUBLISHER_ADMIN === true;
      },
      isAxPublisherOnly: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.AX_PUBLISHER === true && anyPerms.MANAGE_PUBLISHING !== true;
      },
      isAxPublisherAdminOnly: function() {
        const anyPerms = this.getAnyVisibility(true);
        return anyPerms.AX_PUBLISHER_ADMIN === true && anyPerms.MANAGE_PUBLISHING !== true;
      },
      canUseCaltLineItemViewType2: function() {
        const anyPerms = this.getAnyVisibility(true);
        const networkPerms = this.getAllNetworkPermissions();
        return anyPerms.AX_ADVERTISER === true
          || anyPerms.AX_ADVERTISER_ADMIN === true
          || anyPerms.MANAGE_ADVERTISING === true
          || networkPerms.MANAGE_BUSINESS === true;
      },
      getSectionPerms: function(app) {
        const teamPerms = this.getAnyVisibility();
        const networkPerms = this.getAllNetworkPermissions();
        const allPerms = _.merge({}, teamPerms, networkPerms);
        const networkDefaults = LocalNetworkProfile.getDefaults();
        const canDoReachAnalysis = networkDefaults.runReportsOptions.indexOf("REACH_ANALYSIS") > -1;
        return {
          dashboards: teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER === true,
          advertising: teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.MANAGE_BUSINESS || teamPerms.CREATIVE_AGENCY === true || teamPerms.RUN_REPORTS === true || teamPerms.AX_ADVERTISER_READ_ONLY === true || (LocalNetworkProfile.isMarketplacePlatform() && (networkPerms.MANAGE_SYSTEM === true || allPerms.MANAGE_AUDIENCES === true)),
          advertisingEditable: allPerms.AX_ADVERTISER_ADMIN === true || allPerms.MANAGE_ADVERTISING === true || allPerms.MANAGE_BUSINESS === true,
          orders: teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER_READ_ONLY === true,
          ordersEditable: allPerms.AX_ADVERTISER === true || allPerms.MANAGE_ADVERTISING === true,
          lineItems: teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.SELF_SERVICE_OWN_ADVERTISING === true || teamPerms.AX_ADVERTISER_READ_ONLY === true,
          lineItemsEditable: allPerms.AX_ADVERTISER === true || allPerms.MANAGE_ADVERTISING === true,
          printLineItems: allPerms.AX_ADVERTISER === true || allPerms.MANAGE_SYSTEM === true,
          thirdPartyAudiences: LocalNetworkProfile.isMarketplacePlatform() && (networkPerms.MANAGE_SYSTEM === true || allPerms.MANAGE_AUDIENCES === true),
          creatives: teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.CREATIVE_AGENCY === true || teamPerms.AX_ADVERTISER_READ_ONLY === true,
          libraryCreatives: teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER_READ_ONLY === true,
          creativesEditable: allPerms.AX_ADVERTISER === true || allPerms.MANAGE_ADVERTISING === true || teamPerms.CREATIVE_AGENCY === true,
          campaigns: teamPerms.MANAGE_ADVERTISING === true,
          advertisers: networkPerms.MANAGE_BUSINESS === true || teamPerms.AX_ADVERTISER === true || teamPerms.AX_ADVERTISER_READ_ONLY === true,
          advertisersEditable: allPerms.MANAGE_BUSINESS === true || allPerms.AX_ADVERTISER === true || allPerms.AX_ADVERTISER_READ_ONLY === true,
          folios: allPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.CREATIVE_AGENCY === true,
          creativeSets: allPerms.AX_ADVERTISER === true || teamPerms.MANAGE_ADVERTISING === true || teamPerms.CREATIVE_AGENCY === true,
          reachAnalysis: !LocalNetworkProfile.isPureMarketplacePlatform() && (teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER === true || (teamPerms.RUN_REPORTS === true && canDoReachAnalysis)),
          bookingCalendar: teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER === true,
          inventory: teamPerms.AX_PUBLISHER === true || teamPerms.MANAGE_PUBLISHING === true || teamPerms.MANAGE_BUSINESS || teamPerms.AX_PUBLISHER_ADMIN || teamPerms.MANAGE_SYSTEM,
          menuInventory: allPerms.AX_PUBLISHER === true || allPerms.MANAGE_PUBLISHING === true || allPerms.MANAGE_BUSINESS || allPerms.AX_PUBLISHER_ADMIN || allPerms.MANAGE_SYSTEM,
          reports: teamPerms.RUN_REPORTS===true || teamPerms.AX_ADVERTISER===true || teamPerms.AX_PUBLISHER===true,
          menuReports: allPerms.RUN_REPORTS===true,
          menuQueries: allPerms.MANAGE_BUSINESS || allPerms.AX_ADVERTISER_ADMIN || allPerms.AX_ADVERTISER===true || allPerms.MANAGE_PUBLISHING===true || allPerms.AX_PUBLISHER===true,
          menuDesign: app === 'ADVERTISING' && (allPerms.MANAGE_BUSINESS || allPerms.MANAGE_LAYOUTS || allPerms.MANAGE_SYSTEM || allPerms.AX_PUBLISHER),
          menuAdmin: (app === 'ADVERTISING' && (allPerms.MANAGE_BUSINESS || allPerms.MANAGE_LAYOUTS)) || allPerms.MANAGE_SYSTEM || allPerms.DATA_ADMIN || allPerms.POLITICAL_ADMIN === true || (politicalAppOnNetwork() && app === 'POLITICAL' && this.hasNetworkPermission('POLITICAL_ADMIN', null, 'POLITICAL')) || (allPerms.DOWNLOAD_LOGS && !allPerms.AX_ADVERTISER && !allPerms.AX_PUBLISHER) || app === 'DATA',
          admin: networkPerms.MANAGE_SYSTEM === true || networkPerms.MANAGE_LAYOUTS === true || networkPerms.SELF_SERVICE_USER_ADMIN === true || networkPerms.AX_ADVERTISER_ADMIN === true || networkPerms.AX_PUBLISHER_ADMIN === true || networkPerms.POLITICAL_ADMIN === true,
          teams: networkPerms.MANAGE_SYSTEM === true || networkPerms.AX_ADVERTISER_ADMIN === true || networkPerms.POLITICAL_ADMIN === true,
          roles: networkPerms.MANAGE_SYSTEM === true,
          layouts: networkPerms.MANAGE_LAYOUTS === true,
          cdnUploads: networkPerms.MANAGE_SYSTEM === true,
          customEvents: networkPerms.MANAGE_SYSTEM === true,
          referenceData: networkPerms.MANAGE_SYSTEM === true,
          contextServiceConfiguration: networkPerms.MANAGE_SYSTEM === true,
          externalDemandSource: networkPerms.MANAGE_SYSTEM === true,
          tiers: networkPerms.MANAGE_SYSTEM === true,
          network: networkPerms.MANAGE_SYSTEM === true,
          newButton: teamPerms.MANAGE_ADVERTISING === true || teamPerms.AX_ADVERTISER === true || teamPerms.MANAGE_PUBLISHING === true || teamPerms.AX_PUBLISHER === true || teamPerms.SELF_SERVICE_OWN_ADVERTISING === true
        };
      },
      hasNetwork: function(permission, networkId) {
        return ensureInstance() ? instance.hasNetworkPermission(permission, networkId) : false;
      },
      hasDataNetworkPermission: function(permission) {
        return ensureInstance() ? instance.hasNetworkPermission(permission, null, "DATA") : false;
      }
    };
}

@Injectable({
  providedIn: 'root'
})
export class LocalUserPermissions {
  [key: string]: any;

  constructor(LocalNetworkProfile: LocalNetworkProfile) {
    Object.assign(this, createPermissionsService(LocalNetworkProfile));
  }
}
